import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse } from '@/api/types'

export interface RoleVm {
  id: number
  roleName: string
  description: string
  isActive: number
  branchId: number
}

export interface RoleListItem extends RoleVm {
  createdDate?: string | null
}

export interface RoleListResponse {
  totalRecords: number
  pageNumber: number
  pageSize: number
  data: RoleListItem[]
}

class RoleService {
  async getAllRoles(branchId: number, pageNo: number, pageSize: number): Promise<RoleListResponse> {
    const url = endpoints.roles.getAll(branchId, pageNo, pageSize)
    const response = await apiClient.get<ApiResponse<RoleListItem[]> | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      const raw =
        Array.isArray(payload.data) ? payload.data :
        Array.isArray((payload as any).Data) ? (payload as any).Data :
        Array.isArray(payload) ? payload :
        []
      const totalRecords =
        payload.totalRecords ??
        (payload as any).TotalRecords ??
        raw.length ??
        0
      const pageNumber = payload.pageNo ?? payload.pageNumber ?? (payload as any).PageNo ?? pageNo
      const size = payload.pageSize ?? (payload as any).PageSize ?? pageSize

      return {
        totalRecords,
        pageNumber,
        pageSize: size,
        data: (raw as any[]).map(normalizeRoleListItem),
      }
    }

    return { totalRecords: 0, pageNumber: pageNo, pageSize, data: [] }
  }

  async getRoleById(id: number): Promise<RoleVm | null> {
    const url = endpoints.roles.getById(id)
    const response = await apiClient.get<ApiResponse<RoleVm> | any>(url)
    const payload = response.data
    if (payload && typeof payload === 'object') {
      const data = (payload as any).data ?? (payload as any).Data ?? payload
      if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) return null
      return normalizeRoleVm(data)
    }
    return null
  }

  async saveRole(vm: RoleVm): Promise<any> {
    const url = endpoints.roles.save
    const response = await apiClient.post(url, vm)
    return response.data
  }

  async updateRole(vm: RoleVm): Promise<any> {
    const url = endpoints.roles.update
    const response = await apiClient.put(url, vm)
    return response.data
  }
}

function normalizeRoleListItem(item: any): RoleListItem {
  return {
    id: item.id ?? item.Id ?? 0,
    roleName: item.roleName ?? item.RoleName ?? '',
    description: item.description ?? item.Description ?? '',
    isActive: item.isActive ?? item.IsActive ?? 0,
    branchId: item.branchId ?? item.BranchId ?? 0,
    createdDate: item.createdDate ?? item.CreatedDate ?? null,
  }
}

function normalizeRoleVm(item: any): RoleVm {
  return {
    id: item.id ?? item.Id ?? 0,
    roleName: item.roleName ?? item.RoleName ?? '',
    description: item.description ?? item.Description ?? '',
    isActive: item.isActive ?? item.IsActive ?? 0,
    branchId: item.branchId ?? item.BranchId ?? 0,
  }
}

export const roleService = new RoleService()
